import { ButtonRadioGroup } from '@payaca/ambrosia';
import { FunctionComponent, useEffect, useState, type JSX } from 'react';
import { useHistory } from 'react-router-dom';

import './DashboardPage.sass';

import { useUpdateMyPreferences } from '@/api/mutations/user/useUpdateMyPreferences';
import useGetMe from '@/api/queries/me/useGetMe';
import useGetMyAccountFeatures from '@/api/queries/me/useGetMyAccountFeatures';
import { useTranslation } from '@/i18n';
import { KeyIndicatorsData } from '@/ui/components';
import ActionRequiredDraftJobsCarousel from '@/ui/components/actionRequiredDraftJobsCarousel/ActionRequiredDraftJobsCarousel';
import ActionRequiredPaymentOutstandingInvoicesCarousel from '@/ui/components/actionRequiredPaymentOutstandingInvoicesCarousel/ActionRequiredPaymentOutstandingInvoicesCarousel';
import ActionRequiredPendingPaymentsCarousel from '@/ui/components/actionRequiredPendingPaymentsCarousel/ActionRequiredPendingPaymentsCarousel';
import ActionRequiredPendingProposalsCarousel from '@/ui/components/actionRequiredPendingProposalsCarousel/ActionRequiredPendingProposalsCarousel';
import ContentPanel from '@payaca/components/contentPanel/ContentPanel';

import AuthenticatedPageWrapper from '@/ui/layouts/authenticatedPageWrapper/AuthenticatedPageWrapper';
import ResponsiveViewWrapper from '@payaca/components/responsiveViewWrapper/ResponsiveViewWrapper';
import ShineBorder from '@payaca/components/shineBorder/ShineBorder';
import { Helmet } from 'react-helmet';
import DashboardScheduledEventsList from '../../components/dashboardScheduledEventsList/DashboardScheduledEventsList';

const DashboardPage: FunctionComponent = (): JSX.Element | null => {
  const translate = useTranslation();
  const history = useHistory();
  const { preferences } = useGetMe();
  const { data: features, isLoading: isLoadingFeatures } =
    useGetMyAccountFeatures();
  const { mutate: updatePreferences } = useUpdateMyPreferences();
  const useNewDashboard = preferences?.useNewDashboard ?? false;
  // Accounts without this feature (newly-created accounts) cannot use the
  // classic dashboard and are sent to the new one.
  const hasLegacyDashboard = features?.includes('hasLegacyDashboard') ?? false;

  const handleDashboardChange = (value: 'classic' | 'new') => {
    updatePreferences(
      { useNewDashboard: value === 'new' },
      {
        onError: (error) => {
          console.error('Failed to update dashboard preference', error);
        },
      }
    );
  };

  // Show shine animation for 5 seconds on page load
  const [showShine, setShowShine] = useState(true);
  useEffect(() => {
    const timer = setTimeout(() => setShowShine(false), 5000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (isLoadingFeatures) return;
    if (useNewDashboard || !hasLegacyDashboard) {
      history.replace('/dashboard-new');
    }
  }, [useNewDashboard, hasLegacyDashboard, isLoadingFeatures, history]);

  // Avoid flashing the classic dashboard before the redirect above fires.
  if (!isLoadingFeatures && !hasLegacyDashboard) {
    return null;
  }

  return (
    <>
      <Helmet title={translate('pages.dashboard.title')} />
      <AuthenticatedPageWrapper className="dashboard-page">
        <div className="flex w-full flex-col p-4">
          <div className="mx-4 mb-4 flex items-center justify-end gap-3">
            <span className="text-sm text-gray-500">Try the new dashboard</span>
            <div className="relative rounded-lg">
              {showShine && (
                <ShineBorder
                  className="pointer-events-none"
                  borderWidth={2}
                  duration={5}
                  shineColor={['var(--color-primary)']}
                />
              )}
              <ButtonRadioGroup
                value={useNewDashboard ? 'new' : 'classic'}
                onChange={handleDashboardChange}
                sizeVariant="sm"
                options={[
                  { label: 'Classic', value: 'classic' as const },
                  { label: 'New', value: 'new' as const },
                ]}
              />
            </div>
          </div>
          <ResponsiveViewWrapper
            className="dashboard-content-wrapper"
            downBreakpointSm={900}
          >
            <div className="key-indicators-data-container">
              <KeyIndicatorsData />
            </div>
            <div className="events-container-outer">
              <div className="events-container">
                <ContentPanel hasBoxShadow={true}>
                  <DashboardScheduledEventsList />
                </ContentPanel>
              </div>
            </div>

            <div className="requires-action-container-outer">
              <div className="requires-action-container">
                <ContentPanel
                  className="requires-action-container-inner"
                  hasBoxShadow={true}
                >
                  <ActionRequiredPendingPaymentsCarousel />
                  <ActionRequiredPaymentOutstandingInvoicesCarousel />
                  <ActionRequiredPendingProposalsCarousel />
                  <ActionRequiredDraftJobsCarousel />
                </ContentPanel>
              </div>
            </div>
          </ResponsiveViewWrapper>
        </div>
      </AuthenticatedPageWrapper>
    </>
  );
};

export default DashboardPage;
